import { PAC_PREFILL_FIELD_IDS } from '@bariendo/lib/patient-access-script';
import { ELIGIBILITY_QUESTIONNAIRE_URL, readEligibilityIntakeSelections } from '@bariendo/lib/eligibility-intake';
import {
  readSignupWeightRelatedConditions,
  WEIGHT_RELATED_CONDITIONS_ID,
} from '@bariendo/lib/weight-related-conditions';
import { MedplumClient, getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';

export type PacPrefill = Partial<Record<(typeof PAC_PREFILL_FIELD_IDS)[number], string>> & {
  /** The PN card's weight-related conditions; only that card asks for them. */
  [WEIGHT_RELATED_CONDITIONS_ID]?: string[];
};

/**
 * Procedure, step-1 goal and prior procedure as captured by the patient's
 * eligibility-form submission.
 *
 * This was a stub returning `{}`, on the basis that the eligibility stream "had
 * not landed". It had: the form writes a `QuestionnaireResponse` against
 * `eligibility-v21`, and the timeline has been rendering its answers all along.
 * The three fields started empty anyway, and the Intake and billing panel's
 * Service row was blank for every lead without a saved intro-call note.
 *
 * The card's own values win over anything returned here — the caller only fills a
 * field that is still empty — so a rep who has already answered is never
 * overwritten.
 *
 * A read failure returns nothing rather than throwing. A CI rep has
 * QuestionnaireResponse read, but an empty prefill is a card the rep fills in by
 * hand, while a throw is a card that will not open.
 */
export async function readEligibilityPrefill(
  medplum: MedplumClient,
  patient: Patient | undefined
): Promise<PacPrefill> {
  const subject = patient ? getReferenceString(patient) : undefined;
  if (!subject) return {};

  try {
    const response = await medplum.searchOne('QuestionnaireResponse', {
      subject,
      questionnaire: ELIGIBILITY_QUESTIONNAIRE_URL,
      _sort: '-authored',
    });

    const { procedure, goal, prior, market } = readEligibilityIntakeSelections(response);
    const conditions = readSignupWeightRelatedConditions(response);

    return {
      ...(procedure ? { procedure } : {}),
      // `wn_main` is the card's id for the goal at signup.
      ...(goal ? { wn_main: goal } : {}),
      ...(prior ? { prior } : {}),
      // `loc` is the card's id for the market. Safe to offer since BAR-2597 made
      // the Medplum market registry canonical for both the form and the select —
      // before that this value named a market the rep could not have chosen.
      ...(market ? { loc: market } : {}),
      ...(conditions.length > 0 ? { [WEIGHT_RELATED_CONDITIONS_ID]: conditions } : {}),
    };
  } catch {
    return {};
  }
}
